import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { message, Tabs } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { translate } from '../../../core'
import { PurchaseOrderGenerate } from '../graphql/purchase-order.graphql'
import {
  PurchaseOrderEntryContext,
  resolveCalculationRequest,
  usePurchaseOrderEntryState,
} from './PurchaseOrderEntryContext'
import { PurchaseOrderEntryContextBar } from './PurchaseOrderEntryContextBar'
import { PurchaseOrderEntryCart, usePurchaseOrderCart } from './PurchaseOrderEntryCart'
import { PurchaseOrderEntryConfirmModal, PurchaseOrderEntrySuccessModal } from './PurchaseOrderEntryGenerateModals'
import { PurchaseOrderEntryItemSearchStepBackOrder } from './PurchaseOrderEntryItemSearchStepBackOrder'
import { PurchaseOrderEntryItemSearchStepCustomItems } from './PurchaseOrderEntryItemSearchStepCustomItems'
import { PurchaseOrderEntryItemSearchStepMinMax } from './PurchaseOrderEntryItemSearchStepMinMax'
import styles from './PurchaseOrderEntry.module.scss'

const tabLabel = (title: string, count: number): ReactNode => (
  <span className={styles.tabLabel}>
    {title}
    <span className={classNames(styles.tabBadge, count > 0 && styles.tabBadgeActive)}>{count}</span>
  </span>
)

export const PurchaseOrderEntry: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { value, reset } = usePurchaseOrderEntryState()
  const request = useMemo(() => resolveCalculationRequest(value), [value])
  const cart = usePurchaseOrderCart(request)

  const [session, setSession] = useState(0)
  const [reviewing, setReviewing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [generatedPurchaseOrderIds, setGeneratedPurchaseOrderIds] = useState<number[]>([])

  const builderRef = useRef<HTMLDivElement>(null)
  const contextBarRef = useRef<HTMLDivElement>(null)
  const locationKey = useRef(location.key)

  useEffect(() => {
    if (location.key === locationKey.current)
      return

    locationKey.current = location.key
    reset()
    setReviewing(false)
    setConfirming(false)
    setSession(pre => pre + 1)
  }, [location.key, reset])

  useEffect(() => {
    const contextBar = contextBarRef.current
    if (!contextBar || typeof ResizeObserver === 'undefined')
      return

    const observer = new ResizeObserver(() =>
      builderRef.current?.style.setProperty('--po-context-bar-height', `${contextBar.offsetHeight}px`))
    observer.observe(contextBar)

    return () => observer.disconnect()
  }, [])

  const [generate, { loading: generating }] = useMutation(PurchaseOrderGenerate, {
    onCompleted: ({ purchaseOrderGenerate }) => {
      setConfirming(false)

      if (!purchaseOrderGenerate.length) {
        message.warning(translate('po_generate_nothing'))
        return
      }

      setReviewing(false)
      reset()
      setSession(pre => pre + 1)
      setGeneratedPurchaseOrderIds(purchaseOrderGenerate)
    },
    onError: error => message.error(error.message),
  })

  return (
    <PurchaseOrderEntryContext.Provider value={value}>
      <div ref={builderRef} className={classNames('content-size', styles.builder)}>
        <div ref={contextBarRef} className={styles.contextBar}>
          <PurchaseOrderEntryContextBar />
        </div>

        <div className={styles.body}>
          <div className={classNames(styles.sources, reviewing && styles.hidden)}>
            <Tabs
              key={session}
              defaultActiveKey='back-order'
              items={[
                {
                  key: 'back-order',
                  label: tabLabel(translate('po_source_back_order'), cart.countBySource.BackOrder),
                  children: <PurchaseOrderEntryItemSearchStepBackOrder />,
                },
                {
                  key: 'stock-sku',
                  label: tabLabel(translate('po_source_stock_sku'), cart.countBySource.CustomSku),
                  children: <PurchaseOrderEntryItemSearchStepCustomItems />,
                },
                {
                  key: 'min-max',
                  label: tabLabel(translate('po_source_min_max'), cart.countBySource.MinMax),
                  children: <PurchaseOrderEntryItemSearchStepMinMax />,
                },
              ]}
            />
          </div>

          <PurchaseOrderEntryCart
            cart={cart}
            reviewing={reviewing}
            onReview={() => setReviewing(true)}
            onBackToEditing={() => setReviewing(false)}
            onGenerate={() => setConfirming(true)}
          />
        </div>
      </div>

      <PurchaseOrderEntryConfirmModal
        open={confirming}
        cart={cart}
        generating={generating}
        onConfirm={() => generate({ variables: { input: request } })}
        onCancel={() => setConfirming(false)}
      />

      <PurchaseOrderEntrySuccessModal
        purchaseOrderIds={generatedPurchaseOrderIds}
        onViewPurchaseOrder={number => navigate(`/purchase-order/${number}`)}
        onGenerateNew={() => setGeneratedPurchaseOrderIds([])}
        onGoToList={() => navigate('/purchase-order')}
      />
    </PurchaseOrderEntryContext.Provider>
  )
}
